import React, { useState, useRef } from 'react';
import {
  signIn, completeNewPassword, requestPasswordReset, confirmPasswordReset,
  signInEcap, completeNewPasswordEcap, requestPasswordResetEcap, confirmPasswordResetEcap,
} from '../services/authService';
import { Button, TextInput, Field } from '../ui/primitives';
import { IconLogo } from '../ui/icons';
import PasswordResetForm from './PasswordResetForm';

// `ecap` routes auth to the dedicated Movorra Studio pool (the /studio dashboard); default is the existing pool.
// PORTAL-BRAND-1: `tagline` is prop-driven so the company portal can identify itself instead of showing
// Movorra's own strapline. Defaults are today's exact copy, so every other surface is byte-for-byte
// unchanged. (The portal previously had branded sign-in copy via StudioLogin; moving it onto the
// ride-share pool dropped that, and the generic screen made /portal look like the wrong page.)
function Login({ onLogin, onDevMode, ecap = false, tagline = 'The OS for corporate mobility' }) {
  const doSignIn = ecap ? signInEcap : signIn;
  const doCompleteNewPassword = ecap ? completeNewPasswordEcap : completeNewPassword;
  const doRequestReset = ecap ? requestPasswordResetEcap : requestPasswordReset;
  const doConfirmReset = ecap ? confirmPasswordResetEcap : confirmPasswordReset;
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef(null);

  const handleLogoTap = () => {
    tapCountRef.current += 1;
    clearTimeout(tapTimerRef.current);
    if (tapCountRef.current >= 5) {
      tapCountRef.current = 0;
      if (onDevMode) onDevMode();
      return;
    }
    tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0; }, 2000);
  };

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);
  // null = normal sign-in; otherwise { step, notice } for PasswordResetForm.
  const [reset, setReset] = useState(null);
  const [notice, setNotice] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const session = await doSignIn(username, password);
      onLogin(session);
    } catch (err) {
      if (err.code === 'NewPasswordRequired') {
        setNeedsNewPassword(true);
        setCognitoUser(err.cognitoUser);
      } else if (err.code === 'PasswordResetRequiredException') {
        // An admin reset this account; Cognito already emailed a code, so go straight to entering it.
        setReset({
          step: 'confirm',
          notice: 'Your password was reset. Enter the verification code we emailed you and choose a new password.',
        });
      } else {
        setError(err.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const session = await doCompleteNewPassword(cognitoUser, newPassword);
      onLogin(session);
    } catch (err) {
      setError(err.message || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  const openReset = () => {
    setError('');
    setNotice('');
    setReset({ step: 'request', notice: '' });
  };

  const closeReset = () => {
    setReset(null);
    setPassword('');
  };

  // Reset succeeded — sign straight in with the new password; if that somehow fails, fall back to the form.
  const handleResetDone = async (resetUsername, newPass) => {
    setUsername(resetUsername);
    try {
      const session = await doSignIn(resetUsername, newPass);
      onLogin(session);
    } catch (err) {
      setReset(null);
      setPassword('');
      setNotice('Password updated. Sign in with your new password.');
    }
  };

  const header = (subtitle) => (
    <div className="flex flex-col items-center text-center mb-6">
      <button
        type="button"
        onClick={handleLogoTap}
        aria-label="Movorra"
        className="w-14 h-14 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-sm mb-4
          cursor-pointer active:scale-95 transition-all duration-150
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      >
        <IconLogo size={28} />
      </button>
      <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Movorra</h1>
      <p className="text-xs uppercase tracking-[0.14em] text-slate-500 mt-1">
        {tagline}
      </p>
      <p className="text-sm text-slate-600 leading-relaxed mt-3">{subtitle}</p>
    </div>
  );

  const errorBlock = error ? (
    <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">
      {error}
    </div>
  ) : null;

  const noticeBlock = notice ? (
    <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-3 py-2">
      {notice}
    </div>
  ) : null;

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-10 bg-slate-50">
      <div className="w-full max-w-sm bg-white rounded-xl border border-slate-200 shadow-lg p-8 fade-in">
        {reset ? (
          <>
            {header('Reset your password')}
            <PasswordResetForm
              initialUsername={username}
              initialStep={reset.step}
              notice={reset.notice}
              requestReset={doRequestReset}
              confirmReset={doConfirmReset}
              onDone={handleResetDone}
              onCancel={closeReset}
            />
          </>
        ) : needsNewPassword ? (
          <>
            {header('Set a new password to continue')}
            {errorBlock}
            <form onSubmit={handleNewPassword} className="space-y-3">
              <Field label="New password">
                <TextInput
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </Field>
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? 'Setting…' : 'Set password'}
              </Button>
            </form>
          </>
        ) : (
          <>
            {header('Sign in to continue')}
            {noticeBlock}
            {errorBlock}
            <form onSubmit={handleLogin} className="space-y-3">
              <Field label="Username or email">
                <TextInput
                  type="text"
                  placeholder="you@company.com"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                />
              </Field>
              <Field label="Password">
                <TextInput
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button type="button" className="text-sm text-indigo-600 hover:text-indigo-800" onClick={openReset}>
                Forgot password?
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
